import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { financialAPI, productAPI, saleAPI, companyAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SyncStatusBadge from '../components/SyncStatusBadge';
import OfflineNotice from '../components/OfflineNotice';
import './Dashboard.css';

export default function Dashboard() {
  const { user, business, parentCompany, isPro, isEnterprise, hasPermission } = useAuth();
  const canSeeMoney     = hasPermission('view_financials');
  const canSeeCustomers = hasPermission('view_customers');
  const [summary,   setSummary]  = useState(null);
  const [lowStock,  setLowStock] = useState([]);
  const [overdue,   setOverdue]  = useState([]);
  const [loading,   setLoading]  = useState(true);

  // Enterprise: company-level data
  const [companyData,    setCompanyData]    = useState(null);
  const [fund,           setFund]           = useState(null);
  const [selectedId,     setSelectedId]     = useState(null); // null = company view, businessId = that business
  const [upgradeRequest, setUpgradeRequest] = useState(null);
  // The figures below come from the server. With no connection they can't
  // load, so say so rather than showing zeros as if they were real.
  const [offline, setOffline] = useState(!navigator.onLine);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const off = () => setOffline(true);
    const on  = () => { setOffline(false); setReloadKey(k => k + 1); };
    window.addEventListener('offline', off);
    window.addEventListener('online', on);
    return () => { window.removeEventListener('offline', off); window.removeEventListener('online', on); };
  }, []);

  useEffect(() => {
    // Each panel loads independently: one refused or failed request (e.g. a
    // staff member without view_financials) must not blank the whole page.
    // A request that got no answer at all (no response) means no connection.
    let noAnswer = false;
    const soft = (p) => p.then(r => r.data).catch((err) => { if (!err.response) noAnswer = true; return null; });
    const isCompanyOwner = isEnterprise && user?.role === 'owner';
    Promise.all([
      canSeeMoney     ? soft(financialAPI.summary()) : null,
      soft(productAPI.lowStock()),
      canSeeCustomers ? soft(saleAPI.debtors())      : null,
      isCompanyOwner  ? soft(companyAPI.financials()) : null,
      isCompanyOwner  ? soft(companyAPI.fundBalance()) : null,
    ]).then(([sum, low, debt, fin, fundRes]) => {
      setSummary(sum?.summary || null);
      setLowStock(low?.products || []);
      setOverdue((debt?.debtors || []).filter(d => d.is_overdue));
      setCompanyData(fin || null);
      setFund(fundRes?.fund || null);
      setOffline(noAnswer || !navigator.onLine);
    }).finally(() => setLoading(false));

    // Fetch upgrade request status for owners who haven't reached Enterprise yet
    if (user?.role === 'owner' && !isEnterprise) {
      settingsAPI.getUpgradeStatus().then(r => setUpgradeRequest(r.data.request || null)).catch(() => {});
    }
  }, [isEnterprise, user?.role, canSeeMoney, canSeeCustomers, reloadKey]);

  const fmt = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  const businesses = companyData?.businesses || [];
  // null when the businesses use different currencies (they can't be added up)
  const totals = companyData?.totals || {};
  const mixedCurrency = !!companyData?.mixed_currency;
  const isCompanyView = isEnterprise && !!companyData && selectedId === null;
  const selectedBiz   = isEnterprise && selectedId ? businesses.find(b => b.id === selectedId) : null;
  const cur = parentCompany?.currency || business?.currency || '';

  // Resolve display values based on selected view
  let revenue, grossProfit, netProfit, receivables, stockValue;
  if (isCompanyView) {
    revenue     = totals.revenue;
    grossProfit = totals.gross_profit;
    netProfit   = totals.net_profit;
    receivables = totals.receivables;
    stockValue  = totals.stock_value;
  } else if (selectedBiz) {
    revenue     = selectedBiz.revenue;
    grossProfit = selectedBiz.gross_profit;
    netProfit   = selectedBiz.net_profit;
    receivables = selectedBiz.receivables;
    stockValue  = selectedBiz.stock_value;
  } else {
    revenue     = summary?.revenue;
    grossProfit = summary?.gross_profit;
    netProfit   = summary?.net_profit;
    receivables = summary?.receivables;
    stockValue  = summary?.stock_value;
  }

  const finLink = isCompanyView ? '/company/financials' : '/financials/revenue';

  return (
    <div className="page">
      <div className="dashboard-header">
        <div className="dashboard-header-row">
          <h1 className="page-title">
            {isCompanyView ? parentCompany?.name : (selectedBiz?.name || business?.name)}
          </h1>
          <SyncStatusBadge />
        </div>
        <span className="dashboard-currency text-muted">{cur}</span>
      </div>

      {offline && <OfflineNotice message="The totals on this page will update when you're back online." />}

      {/* Upgrade CTA — owners not yet on the top tier */}
      {user?.role === 'owner' && !isEnterprise && (
        upgradeRequest?.status === 'pending' ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
            background: '#1c1a10', border: '1px solid #78350f',
            borderRadius: '14px', padding: '0.9rem 1.1rem', marginBottom: '1rem',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fde68a' }}>
                ⏳ Upgrade request pending
              </div>
              <div style={{ fontSize: '0.8rem', color: '#a16207', marginTop: '0.1rem' }}>
                Your request to upgrade to {upgradeRequest.requested_plan} is being reviewed. We'll notify you once it's approved.
              </div>
            </div>
            <span style={{
              flexShrink: 0, background: '#78350f', borderRadius: '999px',
              padding: '0.4rem 0.9rem', fontWeight: 700, fontSize: '0.75rem',
              color: '#fde68a', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Pending
            </span>
          </div>
        ) : (
          <Link
            to="/upgrade"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
              background: isPro
                ? 'linear-gradient(135deg,#0ea5e9,#6366f1)'
                : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              color: '#fff',
              borderRadius: '14px', padding: '0.9rem 1.1rem', marginBottom: '1rem',
              textDecoration: 'none', boxShadow: '0 6px 20px rgba(99,102,241,0.25)',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {isPro ? 'Upgrade to Enterprise' : '🚀 You\'re on the Starter plan'}
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.1rem' }}>
                {isPro
                  ? 'Run multiple businesses with consolidated financials & fund tracking.'
                  : 'Unlock team members, financial reports, multi-device sync & exports.'}
              </div>
            </div>
            <span style={{
              flexShrink: 0, background: 'rgba(255,255,255,0.2)', borderRadius: '999px',
              padding: '0.4rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap',
            }}>
              {isPro ? 'Upgrade →' : 'See plans →'}
            </span>
          </Link>
        )
      )}

      {/* Business / company selector — Enterprise only */}
      {isEnterprise && businesses.length > 0 && (
        <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button
            onClick={() => setSelectedId(null)}
            style={{
              flexShrink: 0, padding: '0.3rem 0.8rem', borderRadius: '999px',
              border: 'none', cursor: 'pointer', fontSize: '0.8rem',
              fontWeight: isCompanyView ? 700 : 400,
              background: isCompanyView ? 'var(--color-primary)' : 'var(--color-card, #1e293b)',
              color: isCompanyView ? '#fff' : 'var(--color-muted)',
            }}
          >
            🏢 {parentCompany?.name}
          </button>
          {businesses.map(b => {
            const active = selectedId === b.id;
            return (
              <button key={b.id} onClick={() => setSelectedId(b.id)} style={{
                flexShrink: 0, padding: '0.3rem 0.8rem', borderRadius: '999px',
                border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                fontWeight: active ? 700 : 400,
                background: active ? 'var(--color-primary)' : 'var(--color-card, #1e293b)',
                color: active ? '#fff' : 'var(--color-muted)',
              }}>
                {b.name}
              </button>
            );
          })}
        </div>
      )}

      {overdue.length > 0 && (
        <div className="alert-banner">
          <span>⚠️ {overdue.length} overdue debt{overdue.length > 1 ? 's' : ''}</span>
          <Link to="/debtors">View</Link>
        </div>
      )}

      {lowStock.length > 0 && (
        <Link to="/inventory" style={{ textDecoration: 'none' }}>
          <div className="alert-banner alert-warn" style={{ cursor: 'pointer' }}>
            <span>📦 {lowStock.length} product{lowStock.length > 1 ? 's' : ''} low on stock</span>
          </div>
        </Link>
      )}

      {/* Fund balance hero — company view only */}
      {isCompanyView && fund && (
        <div className="card" style={{ marginBottom: '1rem', textAlign: 'center', padding: '1rem' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>
            Available Funds
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: Number(fund.balance) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {cur} {fmt(fund.balance)}
          </div>
          <Link to="/company/fund" style={{ fontSize: '0.78rem', color: 'var(--color-primary)', marginTop: '0.25rem', display: 'inline-block' }}>
            Fund details →
          </Link>
        </div>
      )}

      {isCompanyView && mixedCurrency && (
        <div className="alert-banner alert-warn">
          <span>Your businesses use different currencies ({(companyData?.currencies || []).join(', ')}), so company totals aren't added up. Pick a business above to see its figures.</span>
        </div>
      )}

      {canSeeMoney && <div className="kpi-grid">
        <Link to={finLink} className="card kpi-card kpi-link">
          <div className="kpi-label">Revenue</div>
          <div className="kpi-value">{cur} {fmt(revenue)}</div>
        </Link>
        <Link to={finLink} className="card kpi-card kpi-link">
          <div className="kpi-label">Gross Profit</div>
          <div className="kpi-value" style={{ color: Number(grossProfit) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {cur} {fmt(grossProfit)}
          </div>
        </Link>
        <Link to={isCompanyView ? '/company/financials' : '/debtors'} className="card kpi-card kpi-link">
          <div className="kpi-label">Receivables</div>
          <div className="kpi-value kpi-warn">{cur} {fmt(receivables)}</div>
        </Link>
        <Link to="/inventory" className="card kpi-card kpi-link">
          <div className="kpi-label">Stock Value (at cost)</div>
          <div className="kpi-value">{cur} {fmt(stockValue)}</div>
        </Link>
      </div>}

      {!isCompanyView && <div style={{ marginTop: '0.5rem' }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.6rem' }}>
          Quick Access
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem' }}>
          {[
            hasPermission('manage_sales') && { to: '/sell', icon: '💰', label: 'New Sale' },
            { to: '/inventory',    icon: '📦', label: 'Inventory'    },
            canSeeCustomers && { to: '/debtors',   icon: '📋', label: 'Debts'     },
            canSeeCustomers && { to: '/customers', icon: '👥', label: 'Customers' },
            hasPermission('manage_expenses') && { to: '/expenses', icon: '🧾', label: 'Expenses' },
            { to: '/transactions', icon: '📆', label: 'Transactions' },
            isPro && canSeeMoney && { to: '/financials', icon: '📊', label: 'Financials' },
          ].filter(Boolean).map(({ to, icon, label }) => (
            <Link
              key={to}
              to={to}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '0.35rem', padding: '0.85rem 0.5rem',
                background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', textDecoration: 'none', color: 'var(--color-text)',
              }}
            >
              <span style={{ fontSize: '1.4rem' }}>{icon}</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--color-muted)' }}>{label}</span>
            </Link>
          ))}
        </div>
      </div>}
    </div>
  );
}
